<template>
  <div class="usage-recording-create container">
    <!-- Header -->
    <div class="d-flex align-items-center my-8">
      <button class="btn btn-icon btn-light mr-4" @click="goBack">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div>
        <h4 class="text-dark fw-bold mb-1">Ghi nhận Sử dụng Mới</h4>
        <p class="text-muted m-0">Chọn nhiều mục thuốc, Gói dịch vụ hoặc Dịch vụ cùng lúc</p>
      </div>
    </div>

    <!-- Section 1: Chọn khách hàng -->
    <div class="section-card mb-6">
      <h5 class="section-title mb-4">Chọn đăng ký khách hàng</h5>

      <div v-if="!orderIdFromRoute" class="position-relative">
        <input
          v-model="phoneQuery"
          type="text"
          class="form-control"
          placeholder="Nhập SĐT khách hàng để tìm..."
          @input="onPhoneQueryInput"
        />
        <div v-if="searching" class="text-muted small mt-2">
          <span class="spinner-border spinner-border-sm mr-1"></span>Đang tìm...
        </div>
        <div v-else-if="phoneQuery.trim() && searchResults.length === 0" class="text-muted small mt-2">
          Không tìm thấy khách hàng nào
        </div>
        <ul v-if="searchResults.length" class="search-result-list mt-2">
          <li
            v-for="order in searchResults"
            :key="order.id"
            class="search-result-item"
            @click="onSelectOrderObj(order)"
          >
            {{ bundleOrderLabel(order) }}
          </li>
        </ul>
      </div>

      <div v-if="selectedOrder" class="selected-info-card mt-4">
        <div class="mb-1">
          Khách hàng: <strong class="text-dark">{{ selectedOrder.customerName }}</strong>
        </div>
        <div class="mb-1">
          Gói: <strong class="text-dark">{{ selectedOrder.packageName }}</strong>
        </div>
        <div>
          Hết hạn: <span class="text-muted">{{ formatEndDate(selectedOrder.endDate) }}</span>
        </div>
      </div>
    </div>

    <!-- Section 2: Chọn mục sử dụng (multi-select across all types) -->
    <div v-if="selectedOrder" class="section-card mb-6">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <h5 class="section-title mb-0">Chọn mục sử dụng</h5>
      </div>

      <b-overlay :show="loadingUsableItems" rounded="sm">
        <div v-if="!loadingUsableItems && !tranMedicine.length && !tranService.length && !tranServicePackage.length" class="text-center py-6">
          <i class="fas fa-box-open fa-2x text-muted mb-3 d-block"></i>
          <p class="text-muted">Khách hàng này chưa có mục nào khả dụng.</p>
        </div>

        <div v-else>
          <!-- Tab filter -->
          <div class="type-tabs mb-4">
            <button
              class="type-tab-btn"
              :class="{ active: activeTab === 'all' }"
              @click="activeTab = 'all'"
            >
              Tất cả
              <span class="tab-count">{{ tranMedicine.length + tranService.length + tranServicePackage.length }}</span>
            </button>
            <button
              class="type-tab-btn"
              :class="{ active: activeTab === 'medicine' }"
              @click="activeTab = 'medicine'"
            >
              <i class="fas fa-pills mr-1 text-primary"></i>Thuốc
              <span class="tab-count">{{ tranMedicine.length }}</span>
            </button>
            <button
              class="type-tab-btn"
              :class="{ active: activeTab === 'service_package' }"
              @click="activeTab = 'service_package'"
            >
              <i class="fas fa-layer-group mr-1 text-success"></i>Gói DV chuyên sâu
              <span class="tab-count">{{ tranServicePackage.length }}</span>
            </button>
            <button
              class="type-tab-btn"
              :class="{ active: activeTab === 'service' }"
              @click="activeTab = 'service'"
            >
              <i class="fas fa-stethoscope mr-1 text-purple"></i>Dịch vụ
              <span class="tab-count">{{ tranService.length }}</span>
            </button>
          </div>

          <!-- Thuốc -->
          <div v-if="activeTab === 'all' || activeTab === 'medicine'">
            <div v-if="activeTab === 'all' && tranMedicine.length" class="items-group-label">
              <i class="fas fa-pills text-primary mr-2"></i>Thuốc
            </div>
            <div v-if="tranMedicine.length === 0 && activeTab === 'medicine'" class="text-center py-4 text-muted">Không có thuốc khả dụng</div>
            <div
              v-for="item in tranMedicine"
              :key="'med-' + item.id"
              class="item-card"
              :class="{ 'item-card--selected': isSelected(item.id, 'medicine') }"
              @click="toggleItem(item, 'medicine')"
            >
              <div class="d-flex align-items-center">
             
                <div class="item-icon icon-medicine mr-3">
                  <i class="fas fa-pills"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="item-name">{{ item.name }}</div>
                  <div class="item-meta text-muted">
                    <span v-if="item.strength">{{ item.strength }}</span>
                    <span v-if="item.strength && item.description" class="mx-1">•</span>
                    <span v-if="item.description">{{ item.description }}</span>
                  </div>
                </div>
                <div class="text-right">
                  <div class="item-count">
                    <span class="badge badge-primary">{{ item.isUnlimited ? 'Không giới hạn' : `Còn ${remainingOf(item, 'medicine')} lần` }}</span>
                  </div>
                  <div class="item-price text-success mt-1">{{ formatPrice(item.price) }} VNĐ</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gói DV chuyên sâu -->
          <div v-if="activeTab === 'all' || activeTab === 'service_package'">
            <div v-if="activeTab === 'all' && tranServicePackage.length" class="items-group-label mt-4">
              <i class="fas fa-layer-group text-success mr-2"></i>Gói DV chuyên sâu
            </div>
            <div v-if="tranServicePackage.length === 0 && activeTab === 'service_package'" class="text-center py-4 text-muted">Không có gói DV khả dụng</div>
            <div
              v-for="item in tranServicePackage"
              :key="'pkg-' + item.id"
              class="item-card"
              :class="{ 'item-card--selected': isSelected(item.id, 'service_package') }"
              @click="toggleItem(item, 'service_package')"
            >
              <div class="d-flex align-items-center">
                <div class="item-icon icon-package mr-3">
                  <i class="fas fa-layer-group"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="item-name">{{ item.name }}</div>
                  <div class="item-meta text-muted">
                    <span v-if="item.duration">{{ item.duration }} ngày</span>
                    <span v-if="item.duration && item.description && item.description.trim()" class="mx-1">•</span>
                    <span v-if="item.description && item.description.trim()">{{ item.description.trim() }}</span>
                  </div>
                </div>
                <div class="text-right">
                  <div class="item-count">
                    <span class="badge badge-success">{{ item.isUnlimited ? 'Không giới hạn' : `Còn ${remainingOf(item, 'service_package')} lần` }}</span>
                  </div>
                  <div class="item-price text-success mt-1">{{ formatPrice(item.price) }} VNĐ</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Dịch vụ -->
          <div v-if="activeTab === 'all' || activeTab === 'service'">
            <div v-if="activeTab === 'all' && tranService.length" class="items-group-label mt-4">
              <i class="fas fa-stethoscope text-purple mr-2"></i>Dịch vụ
            </div>
            <div v-if="tranService.length === 0 && activeTab === 'service'" class="text-center py-4 text-muted">Không có dịch vụ khả dụng</div>
            <div
              v-for="item in tranService"
              :key="'svc-' + item.id"
              class="item-card"
              :class="{ 'item-card--selected': isSelected(item.id, 'service') }"
              @click="toggleItem(item, 'service')"
            >
              <div class="d-flex align-items-center">
                <div class="item-icon icon-service mr-3">
                  <i class="fas fa-stethoscope"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="item-name">{{ item.name }}</div>
                  <div class="item-meta text-muted">
                    <span v-if="item.minutes">{{ item.minutes }} phút</span>
                    <span v-if="item.minutes && item.description" class="mx-1">•</span>
                    <span v-if="item.description">{{ item.description }}</span>
                  </div>
                </div>
                <div class="text-right">
                  <div class="item-count">
                    <span class="badge badge-purple">{{ item.isUnlimited ? 'Không giới hạn' : `Còn ${remainingOf(item, 'service')} lần` }}</span>
                  </div>
                  <div class="item-price text-success mt-1">{{ formatPrice(item.price) }} VNĐ</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>

    <!-- Footer actions -->
    <div class="d-flex mb-8">
      <button
        class="btn btn-success mr-3"
        :disabled="selectedItems.length === 0"
        @click="openConfirmModal"
      >
        <i class="fas fa-clipboard-check mr-2"></i>Ghi nhận ({{ selectedItems.length }})
      </button>
      <button class="btn btn-outline-secondary" @click="goBack">Hủy</button>
    </div>

    <!-- ============ Confirm Modal ============ -->
    <b-modal
      id="confirm-usage-modal"
      v-model="showConfirmModal"
      title="Xác nhận ghi nhận sử dụng"
      size="lg"
      centered
      no-close-on-backdrop
      hide-footer
    >
      <div class="confirm-modal-content">
        <!-- Customer info -->
        <div class="confirm-customer-info mb-4">
          <i class="fas fa-user-circle mr-2 text-primary"></i>
          <strong>{{ selectedOrder && selectedOrder.customerName }}</strong>
          <span class="text-muted mx-2">—</span>
          <span class="text-muted">{{ selectedOrder && selectedOrder.packageName }}</span>
        </div>

        <!-- Items list with quantity edit -->
        <div class="confirm-items-list mb-4">
          <div
            v-for="(entry, idx) in confirmList"
            :key="idx"
            class="confirm-item-row"
          >
            <div class="d-flex align-items-center">
              <div class="item-icon mr-3" :class="iconBgClass(entry.itemType)" style="width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:0 0 36px;">
                <i :class="iconClass(entry.itemType)" style="font-size:0.85rem;"></i>
              </div>
              <div class="flex-grow-1">
                <div class="confirm-item-name">{{ entry.name }}</div>
                <div class="confirm-item-type">
                  <span class="type-badge" :class="typeBadgeClass(entry.itemType)">{{ typeLabel(entry.itemType) }}</span>
                  <span class="text-muted ml-2" style="font-size:0.8rem;">{{ entry.isUnlimited ? 'Không giới hạn' : `Còn lại: ${entry.remaining} lần` }}</span>
                </div>
              </div>
              <div class="confirm-qty-control ml-3">
                <button class="qty-btn" @click="decreaseQty(idx)" :disabled="entry.quantity <= 1">
                  <i class="fas fa-minus"></i>
                </button>
                <input
                  v-model.number="entry.quantity"
                  type="number"
                  class="qty-input"
                  :min="1"
                  :max="entry.remaining"
                  @change="clampQty(idx)"
                />
                <button class="qty-btn" @click="increaseQty(idx)" :disabled="entry.quantity >= entry.remaining">
                  <i class="fas fa-plus"></i>
                </button>
              </div>
              <button class="btn btn-sm btn-light ml-3 remove-btn" @click="removeFromConfirm(idx)" title="Bỏ mục này">
                <i class="fas fa-times text-danger"></i>
              </button>
            </div>
            <div v-if="entry.quantity > entry.remaining" class="text-danger mt-1" style="font-size:0.8rem;">
              <i class="fas fa-exclamation-triangle mr-1"></i>Vượt quá số còn lại
            </div>
          </div>
        </div>

        <hr class="my-4" />

        <!-- Date & Note (shared for all) -->
        <div class="row">
          <div class="col-md-6 mb-4">
            <basic-date-picker
              label="Ngày sử dụng"
              placeholder="DD/MM/YYYY"
              name="usageDate"
              format="DD/MM/YYYY"
              :value.sync="usageDate"
              :required="true"
            />
          </div>
          <div class="col-md-6 mb-4">
            <label class="form-label text-dark fw-bold">Ghi chú</label>
            <textarea
              v-model="usageNote"
              class="form-control"
              rows="3"
              placeholder="Ghi chú về lần sử dụng này..."
              style="border-radius: 8px; resize: vertical;"
            ></textarea>
          </div>
        </div>

        <!-- Modal footer actions -->
        <div class="d-flex justify-content-end mt-2">
          <button class="btn btn-outline-secondary mr-3" @click="showConfirmModal = false" :disabled="submitting">
            Hủy
          </button>
          <button
            class="btn btn-success px-5"
            :disabled="!canConfirm || submitting"
            @click="submitUsage"
          >
            <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
            <i v-else class="fas fa-check mr-2"></i>Xác nhận
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ============ Modal chọn UserPackage để map ============ -->
    <b-modal
      id="link-userpackage-modal"
      v-model="linkPackageModal.show"
      title="Chọn gói đăng ký của khách hàng"
      size="md"
      centered
      no-close-on-backdrop
      hide-footer
    >
      <b-overlay :show="linkPackageModal.loading" rounded="sm">
        <div v-if="!linkPackageModal.loading && linkPackageModal.options.length === 0" class="text-center py-4">
          <p class="text-danger mb-3">Người dùng chưa tham gia gói dịch vụ</p>
          <button class="btn btn-outline-secondary" @click="linkPackageModal.show = false">Đóng</button>
        </div>
        <div v-else>
          <basic-select
            label="Gói đăng ký"
            placeholder="-- Chọn gói đăng ký --"
            name="userPackage"
            :options="linkPackageModal.options"
            track-by="value"
            value-label="text"
            :value.sync="linkPackageModal.selectedId"
          />
          <div class="d-flex justify-content-end mt-4">
            <button class="btn btn-outline-secondary mr-3" @click="linkPackageModal.show = false">Hủy</button>
            <button class="btn btn-success" :disabled="!linkPackageModal.selectedId" @click="confirmLinkPackage">
              Xác nhận
            </button>
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
const ITEM_TYPE_MAP = { medicine: 0, service: 2, service_package: 1 };

export default {
  name: 'UsageRecordingCreate',
  data() {
    return {
      loadingOrders: false,
      loadingUsableItems: false,
      orderIdFromRoute: null,
      phoneQuery: '',
      searching: false,
      searchResults: [],
      searchDebounceTimer: null,
      selectedOrder: null,
      activeTab: 'all',
      tranMedicine: [],
      tranService: [],
      tranServicePackage: [],
      // Multi-select: array of { id, name, itemType, remaining }
      selectedItems: [],
      // Confirm modal
      showConfirmModal: false,
      confirmList: [],
      usageDate: new Date().toISOString().slice(0, 10),
      usageNote: '',
      submitting: false,
      // Modal chọn UserPackage để map với item Gói dịch vụ
      linkPackageModal: {
        show: false,
        item: null,
        options: [],
        loading: false,
        selectedId: null,
      },
    };
  },
  computed: {
    canConfirm() {
      if (!this.confirmList.length) return false;
      if (!this.usageDate) return false;
      return this.confirmList.every((e) => e.quantity >= 1 && e.quantity <= e.remaining);
    },
  },
  async mounted() {
    const orderId = this.$route?.query?.orderId;
    if (orderId) {
      this.orderIdFromRoute = orderId;
      await this.loadOrderById(orderId);
    }
  },
  methods: {
    // usable-items returns the item's total quantity; usedCount is what has been consumed.
    remainingOf(item, type) {
      const total = {
        medicine: item.tranMedicineCount,
        service: item.tranServiceCount,
        service_package: item.tranServicePackageCount,
      }[type] || 0;
      return Math.max(0, total - (item.usedCount || 0));
    },
    goBack() {
      this.$router.push({ name: 'usage_recording' });
    },

    bundleOrderLabel(order) {
      const customerName = order?.accountHis?.fullName || order?.accountHis?.firstName || order?.accountHis?.lastName || 'Không rõ';
      const packageName = order?.tranPackage?.name || 'Không rõ gói';
      const phone = order?.accountHis?.phoneMobile ? ` (${order.accountHis.phoneMobile})` : '';
      return `${customerName}${phone} - ${packageName}`;
    },

    onPhoneQueryInput() {
      clearTimeout(this.searchDebounceTimer);
      const q = this.phoneQuery.trim();
      if (!q) {
        this.searchResults = [];
        this.searching = false;
        return;
      }
      this.searchDebounceTimer = setTimeout(() => this.searchByPhone(q), 500);
    },

    async searchByPhone(q) {
      this.searching = true;
      try {
        const res = await this.$api.get('UserBundleOrders/list-active', { params: { phoneNumber: q } });
        const raw = res?.data ?? res;
        this.searchResults = Array.isArray(raw) ? raw : (raw?.data ?? []);
      } catch (error) {
        console.error('Lỗi tìm khách hàng:', error);
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể tìm khách hàng' });
      } finally {
        this.searching = false;
      }
    },

    async loadOrderById(orderId) {
      try {
        this.loadingOrders = true;
        const res = await this.$api.get(`UserBundleOrders/detail?id=${orderId}`);
        const raw = res?.data ?? res;
        const order = raw?.data ?? raw;
        if (!order) {
          this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không tìm thấy đơn đăng ký' });
          return;
        }
        this.setSelectedOrder(order);
      } catch (error) {
        console.error('Lỗi tải chi tiết đơn đăng ký:', error);
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể tải chi tiết đơn đăng ký' });
      } finally {
        this.loadingOrders = false;
      }
    },

    setSelectedOrder(order) {
      this.selectedOrder = {
        id: order.id,
        customerName: order.accountHis?.fullName || order.accountHis?.firstName || 'Không rõ',
        packageName: order.tranPackage?.name || 'Không rõ gói',
        endDate: order.endDate,
        accountId: order.accountId,
        salePackageId: order.salePackageId,
        phoneMobile: order.accountHis?.phoneMobile || '',
      };
      this.activeTab = 'all';
      this.loadUsableItems(order.id);
    },

    onSelectOrderObj(order) {
      if (!order) {
        this.selectedOrder = null;
        this.activeTab = 'all';
        this.resetUsableItems();
        return;
      }
      this.searchResults = [];
      this.phoneQuery = this.bundleOrderLabel(order);
      this.setSelectedOrder(order);
    },

    resetUsableItems() {
      this.tranMedicine = [];
      this.tranService = [];
      this.tranServicePackage = [];
      this.selectedItems = [];
    },

    async loadUsableItems(orderId) {
      try {
        this.loadingUsableItems = true;
        this.resetUsableItems();
        const res = await this.$api.get(`UserBundleOrders/usable-items/${orderId}`);
        const raw = res?.data ?? res;
        this.tranMedicine = raw?.tranMedicine ?? [];
        this.tranService = raw?.tranService ?? [];
        this.tranServicePackage = raw?.tranServicePackage ?? [];
      } catch (error) {
        console.error('Lỗi tải danh sách mục khả dụng:', error);
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể tải danh sách mục khả dụng' });
      } finally {
        this.loadingUsableItems = false;
      }
    },

    isSelected(id, type) {
      return this.selectedItems.some((s) => s.id === id && s.itemType === type);
    },

    toggleItem(item, type) {
      const idx = this.selectedItems.findIndex((s) => s.id === item.id && s.itemType === type);
      if (idx >= 0) {
        this.selectedItems.splice(idx, 1);
        return;
      }
      if (type === 'service_package' && item.servicePackageId && !item.userPackageId) {
        this.openLinkPackageModal(item);
        return; // chưa thêm vào selectedItems ngay, đợi xác nhận map trong modal
      }
      this._addSelectedItem(item, type);
    },

    _addSelectedItem(item, type) {
      let remaining = 0;
      if (item.isUnlimited) {
        remaining = Number.MAX_SAFE_INTEGER;
      } else remaining = this.remainingOf(item, type);
      this.selectedItems.push({ id: item.id, name: item.name, itemType: type, remaining, isUnlimited: !!item.isUnlimited });
    },

    async openLinkPackageModal(item) {
      this.linkPackageModal = { show: true, item, options: [], loading: true, selectedId: null };
      try {
        const res = await this.$api.get('BundleTranServicePackage/available-user-packages', {
          params: { phoneNumber: this.selectedOrder.phoneMobile, servicePackageId: item.servicePackageId },
        });
        const list = res?.data ?? res ?? [];
        this.linkPackageModal.options = list.map((p) => ({
          value: p.id,
          text: `${p.name} — bắt đầu ${this.formatUserPackageDate(p.startDate)}`,
        }));
      } catch (error) {
        console.error('Lỗi tìm gói đăng ký:', error);
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể tìm gói đăng ký của khách' });
      } finally {
        this.linkPackageModal.loading = false;
      }
    },

    async confirmLinkPackage() {
      const { item, selectedId } = this.linkPackageModal;
      if (!selectedId) return;
      try {
        // Controller Edit dùng [FromForm] -> phải gửi FormData, không phải JSON body
        const formData = new FormData();
        formData.append('userPackageId', selectedId);
        await this.$api.put(`BundleTranServicePackage/${item.id}`, formData);
        item.userPackageId = selectedId; // cập nhật local để lần sau không hỏi lại
        this._addSelectedItem(item, 'service_package');
        this.linkPackageModal.show = false;
      } catch (error) {
        console.error('Lỗi lưu liên kết gói đăng ký:', error);
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Không thể lưu liên kết gói đăng ký' });
      }
    },

    openConfirmModal() {
      if (!this.selectedItems.length) return;
      // Build confirmList from selectedItems
      this.confirmList = this.selectedItems.map((s) => ({
        id: s.id,
        name: s.name,
        itemType: s.itemType,
        remaining: s.remaining,
        isUnlimited: s.isUnlimited,
        quantity: 1,
      }));
      this.usageDate = new Date().toISOString().slice(0, 10);
      this.usageNote = '';
      this.showConfirmModal = true;
    },

    increaseQty(idx) {
      const entry = this.confirmList[idx];
      if (entry.quantity < entry.remaining) entry.quantity += 1;
    },

    decreaseQty(idx) {
      const entry = this.confirmList[idx];
      if (entry.quantity > 1) entry.quantity -= 1;
    },

    clampQty(idx) {
      const entry = this.confirmList[idx];
      if (entry.quantity < 1) this.$set(this.confirmList[idx], 'quantity', 1);
      if (entry.quantity > entry.remaining) this.$set(this.confirmList[idx], 'quantity', entry.remaining);
    },

    removeFromConfirm(idx) {
      const removed = this.confirmList.splice(idx, 1)[0];
      // Also deselect in selectedItems
      const si = this.selectedItems.findIndex((s) => s.id === removed.id && s.itemType === removed.itemType);
      if (si >= 0) this.selectedItems.splice(si, 1);
    },

    async submitUsage() {
      if (this.submitting) return;
      if (!this.canConfirm) return;

      try {
        this.submitting = true;

        let usageTimestamp = null;
        if (this.usageDate) {
          // basic-date-picker emits DD/MM/YYYY — normalize to YYYY-MM-DD before parsing
          let normalized = this.usageDate;
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
            const [day, month, year] = normalized.split('/');
            normalized = `${year}-${month}-${day}`;
          }
          const d = new Date(normalized);
          const now = new Date();
          d.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
          usageTimestamp = Math.floor(d.getTime() / 1000);
        }

        const payloadList = this.confirmList.map((entry) => ({
          UserBundleOrderId: this.selectedOrder.id,
          AccountHisId: this.selectedOrder.accountId,
          ItemId: entry.id,
          ItemType: ITEM_TYPE_MAP[entry.itemType] ?? 0,
          Note: this.usageNote || '',
          Status: 0,
          CreateDatetime: usageTimestamp,
          Quantity: entry.quantity,
        }));

        await this.$api.post('ServiceUsages/batch', payloadList, {});

        this.$toastr?.s?.({
          title: 'Thành công',
          msg: `Đã ghi nhận ${payloadList.length} mục sử dụng cho "${this.selectedOrder.customerName}"`,
        });
        this.showConfirmModal = false;
        this.goBack();
      } catch (error) {
        console.error('Lỗi ghi nhận:', error);
        this.$toastr?.e?.({
          title: 'Lỗi',
          msg: 'Ghi nhận sử dụng thất bại. Vui lòng thử lại.',
        });
      } finally {
        this.submitting = false;
      }
    },

    formatUserPackageDate(val) {
      // UserPackage.StartDate được BE trả về dạng unix timestamp giây (giống các field DateTime khác)
      if (!val) return '?';
      const ms = val < 1e10 ? val * 1000 : val;
      const d = new Date(ms);
      if (isNaN(d.getTime())) return '?';
      return this.$moment ? this.$moment(d).format('D/M/YYYY') : d.toLocaleDateString('vi-VN');
    },

    formatEndDate(val) {
      if (!val) return '?';
      const ms = val < 1e10 ? val * 1000 : val;
      const d = new Date(ms);
      if (isNaN(d.getTime())) return '?';
      return this.$moment ? this.$moment(d).format('D/M/YYYY') : d.toLocaleDateString('vi-VN');
    },

    formatPrice(val) {
      if (!val && val !== 0) return '0';
      return new Intl.NumberFormat('vi-VN').format(val);
    },

    typeLabel(type) {
      if (type === 'medicine') return 'Thuốc';
      if (type === 'service_package') return 'Gói DV';
      return 'Dịch vụ';
    },

    typeBadgeClass(type) {
      if (type === 'medicine') return 'badge-type-medicine';
      if (type === 'service_package') return 'badge-type-package';
      return 'badge-type-service';
    },

    iconClass(type) {
      if (type === 'medicine') return 'fas fa-pills';
      if (type === 'service_package') return 'fas fa-layer-group';
      return 'fas fa-stethoscope';
    },

    iconBgClass(type) {
      if (type === 'medicine') return 'icon-medicine';
      if (type === 'service_package') return 'icon-package';
      return 'icon-service';
    },
  },
};
</script>

<style scoped lang="scss">
.usage-recording-create {
  padding: 0;
}

.btn-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #e4e6ef;
  background: #fff;
  transition: all 0.2s;

  &:hover {
    background: #f3f6f9;
    border-color: #d3d3da;
  }
}

.section-card {
  background: #f8f9fb;
  border-radius: 14px;
  padding: 24px 28px;
}

.section-title {
  font-weight: 700;
  color: #181c32;
  font-size: 1rem;
}

/* Phone search results */
.search-result-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.search-result-item {
  padding: 10px 14px;
  font-size: 0.9rem;
  color: #181c32;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f8;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f3f6f9;
  }
}

/* Selected info card */
.selected-info-card {
  background: #eef6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  padding: 14px 18px;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.7;
}

/* Type tabs */
.type-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #e4e6ef;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  color: #5e6278;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    border-color: #b5b5c3;
  }

  &.active {
    border-color: #50cd89;
    background: #f0fdf4;
    color: #17a355;
    font-weight: 600;
  }

  .tab-count {
    background: #e4e6ef;
    color: #5e6278;
    border-radius: 10px;
    padding: 1px 8px;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.6;
  }

  &.active .tab-count {
    background: #d1fae5;
    color: #065f46;
  }
}

/* Group labels */
.items-group-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #7e8299;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e4e6ef;
}

/* Items list */
.item-card {
  background: #fff;
  border: 1.5px solid #e4e6ef;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b5b5c3;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }

  &--selected {
    border-color: #50cd89;
    background: #f0fdf4;
    box-shadow: 0 0 0 1px #50cd89;
  }
}

/* Checkbox circle */
.item-checkbox {
  flex: 0 0 auto;
}
.checkbox-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s;
  color: #fff;
  font-size: 0.65rem;
  background: #fff;

  &.checked {
    border-color: #50cd89;
    background: #50cd89;
  }
}

.item-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  i { font-size: 0.9rem; }
}
.icon-medicine {
  background: #eef6ff;
  color: #3b82f6;
}
.icon-service {
  background: #f4f1ff;
  color: #6f42c1;
}
.icon-package {
  background: #e8fff3;
  color: #17c653;
}

.item-name {
  font-weight: 600;
  color: #181c32;
  font-size: 0.92rem;
}
.item-meta {
  font-size: 0.82rem;
  margin-top: 2px;
}
.item-count {
  font-size: 0.82rem;
}
.item-price {
  font-size: 0.85rem;
  font-weight: 600;
}

.text-purple {
  color: #6f42c1;
}
.badge-purple {
  background: #6f42c1;
  color: #fff;
}

/* ============ Confirm Modal ============ */
.confirm-modal-content {
  padding: 4px 4px 8px;
}

.confirm-customer-info {
  background: #f0f9ff;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 0.92rem;
  color: #374151;
}

.confirm-items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
}

.confirm-item-row {
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 12px;
  padding: 12px 16px;
  transition: border-color 0.15s;
}

.confirm-item-name {
  font-weight: 600;
  color: #181c32;
  font-size: 0.9rem;
  line-height: 1.3;
}

.confirm-item-type {
  display: flex;
  align-items: center;
  margin-top: 3px;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge-type-medicine {
  background: #eef6ff;
  color: #3b82f6;
}
.badge-type-package {
  background: #e8fff3;
  color: #17c653;
}
.badge-type-service {
  background: #f4f1ff;
  color: #6f42c1;
}

/* Quantity control */
.confirm-qty-control {
  display: flex;
  align-items: center;
  gap: 0;
  background: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.qty-btn {
  width: 32px;
  height: 34px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #374151;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.qty-input {
  width: 48px;
  border: none;
  background: transparent;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #111827;
  outline: none;
  padding: 4px 0;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.remove-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}
</style>
